import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';

import { Box, Button, Flex, Heading, SimpleGrid, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { SectionHeader } from '~/components/section-header';
import { trackEvent } from '~/features/analytics/track-event';
// Deliberate dependency: CatalogCard stays in the experiment folder until the
// PLP experiment graduates; the school theme is forced via `themeOverride`.
import { CatalogCard } from '~/features/experiments/ab_plp_redesign_2025/components/catalog-card';
import { SCHOOLS } from '~/features/school/constants';

import { SCHOOL_LP_ANALYTICS_EVENT } from '../constants';
import { SchoolLpContent } from '../models/school-lp-content';
import { SchoolLpProgram } from '../models/school-lp-program';

import { SchoolEarningsBand } from './school-earnings-band';

type RecommendedProgramsSectionProps = NonNullable<StaticRecommendedProgramsSectionProps>;

/** Programs shown before the "show all" control appears (two desktop rows). */
export const RECOMMENDED_PROGRAMS_INITIAL_COUNT = 8;

/** Slices the grid to the visible window and reports the expand/collapse state. */
export function getVisiblePrograms(programs: SchoolLpProgram[], visibleCount: number) {
  return {
    visiblePrograms: programs.slice(0, visibleCount),
    hasMore: programs.length > visibleCount,
    canCollapse: visibleCount > RECOMMENDED_PROGRAMS_INITIAL_COUNT,
  };
}

export function RecommendedProgramsSection({
  eyebrow,
  title,
  description,
  programs,
  cta,
  earningsBand,
  shieldImage,
  theme,
  themeOverride,
}: RecommendedProgramsSectionProps) {
  const [visibleCount, setVisibleCount] = useState(RECOMMENDED_PROGRAMS_INITIAL_COUNT);
  const { visiblePrograms, hasMore, canCollapse } = getVisiblePrograms(programs, visibleCount);

  const handleShowAll = () => {
    setVisibleCount(programs.length);
    trackEvent({
      name: SCHOOL_LP_ANALYTICS_EVENT,
      type: 'click',
      label: 'show_all_programs_clicked',
    });
  };

  const handleShowLess = () => {
    setVisibleCount(RECOMMENDED_PROGRAMS_INITIAL_COUNT);
    trackEvent({
      name: SCHOOL_LP_ANALYTICS_EVENT,
      type: 'click',
      label: 'show_less_programs_clicked',
    });
  };

  return (
    <Box as='section' bg='white' padding={{ base: '64px 0', md: '88px 0 112px' }}>
      <ContentContainer contentMaxWidth='content-max-width-lg'>
        <Flex alignItems='center' flexDirection='column' gap={{ base: '40px', md: '56px' }}>
          <SectionHeader
            color='grey.500'
            containerProps={{ maxWidth: '842px' }}
            iconSlot={shieldImage ? <Image alt='' height={20} src={shieldImage} width={18} /> : undefined}
            subtitle={eyebrow}
            descriptionSlot={
              <Text color='grey.500' fontSize={{ base: '0.875rem', md: '1rem' }} lineHeight='1.5'>
                {description}
              </Text>
            }
            titleSlot={
              <Heading as='h2' color='grey.900' size={{ base: 'mobile-heading-2', md: 'heading-2' }}>
                {title}
              </Heading>
            }
          />
          <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={{ base: '16px', md: '24px' }} width='100%'>
            {visiblePrograms.map((program) => (
              <CatalogCard key={program.key} catalogItem={program} themeOverride={themeOverride} grow />
            ))}
          </SimpleGrid>
          {hasMore && (
            <Button
              _active={{ bg: 'orange.100' }}
              _hover={{ bg: 'orange.50' }}
              alignItems='center'
              bg='transparent'
              borderColor={theme.ctaColor}
              borderStyle='solid'
              borderWidth='1.5px'
              color={theme.ctaColor}
              display='inline-flex'
              fontSize='0.9375rem'
              fontWeight='500'
              justifyContent='center'
              paddingX='24px'
              paddingY='14px'
              variant='unstyled'
              width={{ base: '100%', md: 'fit-content' }}
              onClick={handleShowAll}
            >
              Show all programs
            </Button>
          )}
          {canCollapse && (
            <Button
              _active={{ bg: 'orange.100' }}
              _hover={{ bg: 'orange.50' }}
              alignItems='center'
              bg='transparent'
              borderColor={theme.ctaColor}
              borderStyle='solid'
              borderWidth='1.5px'
              color={theme.ctaColor}
              display='inline-flex'
              fontSize='0.9375rem'
              fontWeight='500'
              justifyContent='center'
              paddingX='24px'
              paddingY='14px'
              variant='unstyled'
              width={{ base: '100%', md: 'fit-content' }}
              onClick={handleShowLess}
            >
              Show fewer programs
            </Button>
          )}
          {cta && (
            <Button
              _active={{ bg: 'orange.100' }}
              _hover={{ bg: 'orange.50' }}
              alignItems='center'
              as={Link}
              bg='transparent'
              borderColor={theme.ctaColor}
              borderStyle='solid'
              borderWidth='1.5px'
              color={theme.ctaColor}
              display='inline-flex'
              fontSize='0.9375rem'
              fontWeight='500'
              href={cta.href}
              justifyContent='center'
              paddingX='24px'
              paddingY='14px'
              variant='unstyled'
              width={{ base: '100%', md: 'fit-content' }}
              onClick={() =>
                trackEvent({
                  name: SCHOOL_LP_ANALYTICS_EVENT,
                  type: 'click',
                  label: 'explore_all_programs_clicked',
                })
              }
            >
              {cta.label}
            </Button>
          )}
          {earningsBand && <SchoolEarningsBand {...earningsBand} />}
        </Flex>
      </ContentContainer>
    </Box>
  );
}

export function transformStaticRecommendedProgramsSectionProps({
  content,
  programsByKey,
  featuredProgramKeys,
  schoolSlug,
}: {
  content: SchoolLpContent;
  programsByKey: Record<string, SchoolLpProgram>;
  /** Sanity school entry's `featuredPrograms` keys, rendered in that order. */
  featuredProgramKeys: string[];
  /** Sanity school entry's slug, used to look up the school shield badge. */
  schoolSlug: string;
}) {
  if (!content.recommendedPrograms || featuredProgramKeys.length === 0) {
    return null;
  }

  const { eyebrow, title, description, cta, earningsBand } = content.recommendedPrograms;

  return {
    eyebrow,
    title,
    description,
    cta,
    programs: featuredProgramKeys.map((key) => programsByKey[key]).filter(Boolean),
    shieldImage: SCHOOLS.find((school) => school.slug === schoolSlug)?.iconPath ?? null,
    earningsBand: earningsBand
      ? {
          ...earningsBand,
          theme: {
            gradient: content.theme.earningsBand.gradient,
            textureImage: content.theme.earningsBand.textureImage,
            statRuleColor: content.theme.colors.accentLight,
            disclaimerColor: content.theme.colors.accentSubtle,
          },
        }
      : null,
    theme: {
      ctaColor: content.theme.colors.accentDark,
    },
    // The catalog taxonomy assigns these programs to other schools, so the
    // whole card theme is forced to the school being browsed.
    themeOverride: {
      accentColor: content.theme.colors.accent,
      titleColor: content.theme.colors.accentDark,
      iconPath: content.theme.programCardBadgeImage,
    },
  };
}

export type StaticRecommendedProgramsSectionProps = ReturnType<typeof transformStaticRecommendedProgramsSectionProps>;
